import { DateObject, ExtendedFile, OcrResult } from './interfaces';
import { DocumentStatus } from './status';
import {
  getDocumentOptions,
  updateStateArray,
  intervalContainsYear,
  getTranslation,
} from './formatter';
import { ocrTypeMap } from './constants';

const SLIDE_DURATION = 300;

const filterExtractionStatus = (result: OcrResult, allowed: string[]) =>
  result?.status && allowed.includes(result.status) ? result : null;

function isOcrReadable(result: OcrResult): boolean {
  return !!result?.document_readable;
}

function isOcrVerified(result: OcrResult): boolean {
  return isCandidateNameVerified(result) && isEmployerNameVerified(result);
}

function isCandidateNameVerified(result: OcrResult): boolean {
  return !!result?.candidate_name_verified;
}

function isEmployerNameVerified(result: OcrResult): boolean {
  return !!result?.employer_name_verified;
}

export const checkOcrResults = (result: OcrResult) => {
  const succeededResult = filterExtractionStatus(result, ['succeeded']);
  const failedResult = filterExtractionStatus(result, [
    'failed',
    'expired',
    'needs_review',
  ]);
  return {
    succeededResult,
    failedResult,
    // Surfaces the wire-provided reason a terminal extraction failed (EVERI-3896)
    // so the UI can show a copy that matches what actually went wrong instead of
    // collapsing every failure into the "doesn't look like a %{types}" message.
    failureReason: failedResult?.failure_reason ?? null,
    isReadable: isOcrReadable(succeededResult),
    isVerified:
      isOcrReadable(succeededResult) && isOcrVerified(succeededResult),
    isCandidateNameVerified: isCandidateNameVerified(succeededResult),
    isEmployerNameVerified: isEmployerNameVerified(succeededResult),
  };
};

export const getDocumentStatus = (
  ocr: any,
  isVerified: boolean,
  isFailed: boolean,
  aiOcrEnabled: boolean,
): DocumentStatus => {
  if (!ocr.completed) return DocumentStatus.Pending;
  if (!ocr.readable) return DocumentStatus.Unreadable;
  if (isFailed) return DocumentStatus.Unverified;
  // First timeout shows Retry; after a retry (timeoutRetried) a second timeout becomes Edited
  // so the manual type/year dropdowns appear instead of looping on Retry.
  if (ocr.timeout && !ocr.timeoutRetried) return DocumentStatus.Unverified;
  if (ocr.timeout) return DocumentStatus.Edited;
  if (ocr.readable && !isVerified && aiOcrEnabled) {
    if (!ocr.candidateVerified && !ocr.employerVerified)
      return DocumentStatus.NeitherVerified;
    if (!ocr.candidateVerified) return DocumentStatus.CandidateUnverified;
    return DocumentStatus.EmployerUnverified;
  }
  if (!isVerified && aiOcrEnabled) return DocumentStatus.Unverified;
  if (ocr.yearOutOfRange && aiOcrEnabled) return DocumentStatus.YearOutOfRange;
  if (ocr.inEditMode) return DocumentStatus.Edited;
  return DocumentStatus.Verified;
};

export const handleSuccessResult = async (
  ocrResult: any,
  file: ExtendedFile,
  i: number,
  documentTypes: any,
  setState: any,
  setOcr: any,
  stateRef: any,
  handleDocumentTypeChange: any,
  handleYearChange: (selectedYearValue: DateObject) => void,
) => {
  const { doc_type_value, year_value } = ocrResult.succeededResult;
  const { uploadId } = file;

  const typeOption = getDocumentOptions(documentTypes).find(
    opt => opt.value === ocrTypeMap[doc_type_value ?? ''],
  );

  if (ocrResult.isVerified) {
    const yearOption = stateRef.current.years.find(
      (year: any) =>
        intervalContainsYear(year.value, year_value) && !year.disabled,
    );

    // EVERI-3428: when Extend confidently identified a recognized, required
    // type AND returned a year that matches no open required slot, lock the
    // row as supporting documentation *synchronously* — route it to the
    // non-required "other" type so it persists as a multi_documents doc and
    // never satisfies a required slot. Locking here (rather than in the
    // deferred setTimeout below) closes the window where the row would briefly
    // show editable dropdowns + Save, which would let a candidate override the
    // wrong year. Requires an actual extracted year (`year_value`): a
    // recognized type with no year falls through to the editable path so the
    // candidate can fill it in.
    if (
      !yearOption &&
      year_value &&
      typeOption &&
      typeOption.requiredForVerification !== false
    ) {
      const otherTypeOption = getDocumentOptions(documentTypes).find(
        opt => opt.value === ocrTypeMap.other,
      );
      if (otherTypeOption) {
        handleDocumentTypeChange({ ...otherTypeOption, uploadId });
      }
      setOcr((prev: any) => ({
        ...prev,
        inEditMode: false,
        yearOutOfRange: true,
        // doc-type label for the InlineError title interpolation (e.g. "W-2")
        ocrDocTypeLabel: getTranslation(typeOption.value),
      }));
      return;
    }

    if (typeOption) {
      handleDocumentTypeChange({ ...typeOption, uploadId });
    } else {
      updateStateArray(setState, 'hasMissingDocuments', true);
      updateStateArray(setState, 'selectedDocumentTypes', '', i);
      setOcr((prev: any) => ({ ...prev, inEditMode: true }));
    }

    setTimeout(() => {
      if (yearOption) {
        handleYearChange({ ...yearOption, uploadId });
        typeOption &&
          setOcr((prev: any) => ({
            ...prev,
            inEditMode: false,
            yearOutOfRange: false,
          }));
      } else {
        // Recognized type with no matching open slot but no extracted year
        // (or a non-required type): leave the row editable for manual fill.
        setOcr((prev: any) => ({
          ...prev,
          inEditMode: true,
          yearOutOfRange: false,
        }));
      }
    }, Math.floor(2 * Math.random() * SLIDE_DURATION));
  } else if (!ocrResult.isReadable) {
    setOcr((prev: any) => ({ ...prev, readable: false, success: false }));
  } else {
    setOcr((prev: any) => ({
      ...prev,
      readable: true,
      success: false,
      inEditMode: false,
    }));
  }
};
